/**
 * SellerReviewsPage - View and manage reviews for sellers
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Star, MessageSquare, User, Calendar, ThumbsUp,
  TrendingUp, Award, Loader2, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star Rating Display
const StarRating = ({ rating, size = 'md' }) => {
  const sizeClass = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <Star 
          key={star}
          className={`${sizeClass} ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
};

// Review Card Component
const ReviewCard = ({ review }) => (
  <Card data-testid={`review-card-${review.id}`}>
    <CardContent className="p-4">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
          {review.reviewer_name?.charAt(0)?.toUpperCase() || 'A'}
        </div>
        
        <div className="flex-1">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold">{review.reviewer_name || 'Anonymous'}</p>
              <StarRating rating={review.rating} size="sm" />
            </div>
            <span className="text-sm text-gray-500">
              {new Date(review.created_at).toLocaleDateString()}
            </span>
          </div>
          
          {review.title && (
            <p className="font-medium mt-2">{review.title}</p>
          )}
          
          {review.comment && (
            <p className="text-gray-600 mt-1">{review.comment}</p>
          )}
          
          <div className="flex items-center gap-4 mt-3">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <BookOpen className="w-3 h-3" />
              {review.listing_title}
            </span>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Rating Distribution Bar
const RatingBar = ({ rating, count, total }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  
  return (
    <div className="flex items-center gap-2">
      <span className="w-8 text-sm text-gray-600">{rating} star</span>
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-amber-400 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="w-8 text-sm text-gray-500 text-right">{count}</span>
    </div>
  );
};

export default function SellerReviewsPage() {
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [email, setEmail] = useState('');
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  const fetchReviews = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    
    try {
      const response = await fetch(`${API_URL}/api/books-media/reviews/seller/${email}?country=${country}`);
      const data = await response.json();
      setReviews(data.reviews || []);
      setStats({
        total: data.total || 0,
        average: data.avg_rating || 0,
        distribution: data.rating_distribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      });
    } catch (err) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-white/20 rounded-xl">
              <Star className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Seller Reviews</h1>
              <p className="text-white/80">View your ratings and customer feedback</p>
            </div>
          </div>
          
          {/* Email Input */}
          <div className="flex gap-3 max-w-md">
            <Input
              type="email"
              placeholder="Enter your seller email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white text-gray-900"
              onKeyDown={(e) => e.key === 'Enter' && fetchReviews()}
              data-testid="seller-email-input"
            />
            <Button onClick={fetchReviews} className="bg-white text-orange-600 hover:bg-gray-100">
              View Reviews
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : !searched ? (
          <Card className="text-center py-16">
            <CardContent>
              <Star className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Enter Your Email</h2>
              <p className="text-gray-500">Enter your seller email above to view your reviews</p>
            </CardContent>
          </Card>
        ) : reviews.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Reviews Yet</h2>
              <p className="text-gray-500 mb-6">Complete sales to start receiving reviews!</p>
              <Button onClick={() => navigate('/books-media')}>
                <BookOpen className="w-4 h-4 mr-2" />
                Browse Marketplace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Stats Sidebar */}
            <div className="lg:col-span-1">
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-500" />
                    Review Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Overall Rating */}
                  <div className="text-center">
                    <div className="text-5xl font-bold text-amber-500">
                      {stats?.average?.toFixed(1) || '0.0'}
                    </div>
                    <StarRating rating={Math.round(stats?.average || 0)} size="lg" />
                    <p className="text-sm text-gray-500 mt-1">
                      Based on {stats?.total || 0} reviews
                    </p>
                  </div>
                  
                  {/* Rating Distribution */}
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map(rating => (
                      <RatingBar
                        key={rating}
                        rating={rating}
                        count={stats?.distribution?.[rating] || 0}
                        total={stats?.total || 0}
                      />
                    ))}
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="pt-4 border-t grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-500">
                        {stats?.distribution?.[5] || 0}
                      </p>
                      <p className="text-xs text-gray-500">5-Star Reviews</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-500">
                        {((stats?.distribution?.[5] || 0) / Math.max(stats?.total || 1, 1) * 100).toFixed(0)}%
                      </p>
                      <p className="text-xs text-gray-500">Satisfaction</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Reviews List */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">All Reviews</h2>
                <Badge variant="secondary">{stats?.total || 0} total</Badge>
              </div>
              
              {reviews.map(review => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          </div>
        )}
        
        {/* Quick Actions */}
        {searched && reviews.length > 0 && (
          <Card className="mt-6">
            <CardContent className="p-6">
              <div className="flex flex-wrap gap-4 justify-center">
                <Button onClick={() => navigate('/books-media/analytics')}>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  View Analytics
                </Button>
                <Button variant="outline" onClick={() => navigate('/books-media/verification')}>
                  <Award className="w-4 h-4 mr-2" />
                  Get Verified
                </Button>
                <Button variant="outline" onClick={() => navigate('/books-media')}>
                  <BookOpen className="w-4 h-4 mr-2" />
                  View Marketplace
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
